import { AdminProjects } from '@platform/client';
import { AdminProjectMatchRecord } from '@platform/client/schemas';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useContext, useMemo } from 'react';
import { formatDateStr } from '../../../dashboard/my-projects/utils';
import { ProfessionalLink } from '../professionals/ProfessionalsAdmin';
import { NoRecordsFoundTabContent, ProfileTabContainer } from '../../components/ProfileComponents';
import { AdminTable, AdminTableContext } from '../../AdminTable';
import { IntroductionLink } from '../introductions/IntroductionsAdmin';

const columnHelper = createColumnHelper<AdminProjectMatchRecord>();

export const MatchTabContent = ({ projectId }: { projectId: number }) => {
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const {
    data: matchData,
    isError,
    isLoading,
    isSuccess,
  } = AdminProjects.useGetProjectMatchData({
    project_id: projectId,
    page: adminQueryOptionsV2.page,
    limit: adminQueryOptionsV2.limit,
    sort: adminQueryOptionsV2.sort,
  });
  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('introductionId', {
          header: 'Intro Id',
          cell: (info) => (
            <IntroductionLink
              introductionId={info.getValue()}
              introductionName={info.getValue().toString()}
            />
          ),
          size: 5,
        }),
        columnHelper.accessor('introductionInitiationType', {
          header: 'Intro Type',
          cell: (info) => (info.getValue() === 'Organization' ? 'Sent' : 'Received'),
          size: 10,
        }),
        columnHelper.accessor('introductionCreatedAt', {
          header: 'Date',
          cell: (info) => formatDateStr(info.getValue()),
          size: 1,
        }),
        columnHelper.accessor('representativeName', {
          header: 'Representative',
          cell: (info) => info.getValue(),
        }),
        columnHelper.accessor('professionalName', {
          header: 'Professional',
          cell: ({ row }) => (
            <ProfessionalLink
              userId={row.original.professionalId}
              name={row.original.professionalName}
            />
          ),
        }),
        columnHelper.accessor('myProjectsStatus', {
          header: 'Status',
          cell: (info) => info.getValue(),
        }),
        columnHelper.accessor('myProjectsStatusDate', {
          header: 'Status date',
          cell: (info) => formatDateStr(info.getValue()),
          size: 10,
        }),
      ] as ColumnDef<AdminProjectMatchRecord>[],
    [],
  );

  return (
    <ProfileTabContainer>
      {isSuccess && matchData?.matchRecords.length === 0 ?
        <NoRecordsFoundTabContent recordType='matches' />
      : <AdminTable
          isError={isError}
          isLoading={isLoading}
          isSuccess={isSuccess}
          columns={columns}
          data={matchData?.matchRecords || []}
          isSimple
          filteredCount={matchData?.totalCount ?? 0}
        />
      }
    </ProfileTabContainer>
  );
};
